import { useKeyboardControls } from "@react-three/drei";
import { useState, useEffect, useRef } from "react";
import sound from './background_music_compressed.mp3';
import clickSound from './click_Sound.mp3';
import '../styles/style.css'
import { useTheme } from "../contexts/ThemeContext";
import { useModal } from "../contexts/ModalContext";
import Info from "../components/billboard_components/Info";
import { useDimension } from "../contexts/DimensionalContext";
import wooshSoundInstance from "../utils/consts/woosh";

// audio
const backgroundMusic = new Audio(sound);
backgroundMusic.volume = 0.5;
backgroundMusic.loop = true;

const clickSoundInstance = new Audio(clickSound);
clickSoundInstance.volume = 0.5;

export default function Interface()
{
    const forward = useKeyboardControls((state) => state.forward)
    const backward = useKeyboardControls((state) => state.backward)
    const leftward = useKeyboardControls((state) => state.leftward)
    const rightward = useKeyboardControls((state) => state.rightward)
    const jump = useKeyboardControls((state) => state.jump)

    const [mute, setMute] = useState(false);
    const musicRef = useRef(backgroundMusic);

    // Start screen refs
    const startButton = useRef(HTMLButtonElement);
    const startContainer = useRef(HTMLDivElement);
    const loadingBar = useRef(HTMLButtonElement);
    const loadingText = useRef(HTMLButtonElement);

    // custom hooks
    const { theme, toggleTheme } = useTheme();
    const { openModal } = useModal();
    const { portfolioDimension, toggleDimension, isTransitioning } = useDimension();
    
    // click sound
    const playClickSound = () => {
        clickSoundInstance.currentTime = 0; 
        clickSoundInstance.play();
    };

    const playWooshSound = () => {
        wooshSoundInstance.currentTime = 0;
        wooshSoundInstance.play();
    }

    // Start Screen Transition
    useEffect(() => {
        startContainer.current.style.opacity = '1'

        // loading completion
        setTimeout(() => {
            loadingBar.current.style.width = '100%';
            loadingText.current.textContent = 'Ready!';
        }, 3000);
    }, []);

    const startPortfolio = () => {
        // exit animation
        startContainer.current.style.opacity = '0';
        musicRef.current.play()
        playClickSound();
        
        // Hide start container after animation completes
        setTimeout(() => {
            startContainer.current.style.display = 'none';
            window.gameStarted = true;
        }, 100);
    }

    const handleClick = (e) => {
        e.preventDefault();
        playClickSound();
        setMute(!mute);
        if(mute) {
            musicRef.current.play();
        } else {
            musicRef.current.pause();
        }
    }

    const handleToggleTheme = (e) => {
        e.preventDefault();
        playClickSound();
        toggleTheme(prev => !prev)
    }

    const handleToggleInfo = (e) => {
        e.preventDefault();
        playClickSound();
        openModal(
            <Info />,
            ""
        )
    }

    const handleToggleDimension = (e) => {
        e.preventDefault();
        playClickSound();
        playWooshSound();
        toggleDimension(prev => !prev);
    }

    return (
        <div className="uiContainer">
            {/* <!-- Welcome Screen -->
            <!-- Probably going to create an svg loading animation --> */}
            <div ref={startContainer} className="startContainer">
                {/* <!-- Stars Background --> */}
                <div className="starsBackground"></div>
                <div className="diagonalStars"></div>
                
                {/* <!-- Animated Stars --> */}
                <div className="animatedStar" style={{top: '5%', left: '10%'}}>✦</div>
                <div className="animatedStar" style={{top: '20%', left: '80%'}}>✧</div>
                <div className="animatedStar" style={{top: '50%', left: '30%'}}>✦</div>
                <div className="animatedStar" style={{top: '70%', left: '60%'}}>✧</div>
                <div className="animatedStar" style={{top: '15%', left: '45%'}}>✦</div>
                <div className="animatedStar" style={{top: '65%', left: '90%'}}>✧</div>
                <div className="animatedStar" style={{top: '40%', left: '75%'}}>✦</div>
                <div className="animatedStar" style={{top: '80%', left: '65%'}}>✧</div>
                
                <div className="animatedStar" style={{top: '10%', left: '90%'}}>✦</div>
                <div className="animatedStar" style={{top: '30%', left: '55%'}}>✧</div>
                <div className="animatedStar" style={{top: '45%', left: '85%'}}>✦</div>
                <div className="animatedStar" style={{top: '55%', left: '65%'}}>✧</div>
                <div className="animatedStar" style={{top: '75%', left: '15%'}}>✦</div>
                <div className="animatedStar" style={{top: '85%', left: '50%'}}>✧</div>
                <div className="animatedStar" style={{top: '35%', left: '5%'}}>✦</div>
                <div className="animatedStar" style={{top: '60%', left: '40%'}}>✧</div>
                
                <div className="startContent">
                    <h1 className="startName">Deris O'Malley</h1>
                    <button ref={startButton} onClick={startPortfolio} className="startButton">Start</button>
                    <div className="innerCircle"></div>

                    {/* <!-- Add ripple elements --> */}
                    <div className="ripple"></div>
                    <div className="ripple"></div>
                    <div className="ripple"></div>

                    {/* <!-- Loading elements --> */}
                    <div ref={loadingBar} className="loadingBar"></div>
                    <div ref={loadingText} className="loadingText">Loading Portfolio Experience...</div>
                </div>
            </div>
            
            {/* Main Interface */}
            <div className="interface">
                {/* <!-- Control Information --> */}
                <div style={portfolioDimension ? {display: 'block'} : {display: 'none'}} className="controlsContainer">
                    <div id="buildControl">
                        <p>Place Turtle with "f"</p>
                        <p>Sprint with "shift"</p>
                    </div>
                </div>

                {/* Controls */}
                <div style={portfolioDimension ? {display: 'block'} : {display: 'none'}} className="controls">
                    <div className="raw">
                        <div className={ `key ${forward ? 'active' : ''}` }></div>
                    </div>
                    <div className="raw">
                        <div className={ `key ${leftward ? 'active' : ''}` }></div>
                        <div className={ `key ${backward ? 'active' : ''}` }></div>
                        <div className={ `key ${rightward ? 'active' : ''}` }></div>
                    </div>
                    <div className="raw">
                        <div className={ `key large ${jump ? 'active' : ''}` }></div>
                    </div>
                </div> 

                {/* Music Sound Button */}
                <div className="musicButtonContainer" onClick={handleClick}>
                    <div style={{ cursor: 'pointer'}} className={`musicButton ${mute ? 'active' : ''}`}></div>
                </div>

                {/* Toggle Theme Button */}
                <div className="themeButtonContainer">
                    <div style={{ cursor: 'pointer'}} onClick={handleToggleTheme} className={`themeButton ${theme === "light" ? 'light' : 'dark'}`}></div>
                </div>

                {/* Toggle Info Button */}
                <div style={portfolioDimension ? {display: 'block'} : {display: 'none'}} className="infoButtonContainer">
                    <div style={{ cursor: 'pointer'}} onClick={handleToggleInfo} className="infoButton"></div>
                </div>

                {/* Toggle Button for Switching to 2D Portfolio Experience */}
                <div className="dimensionButtonContainer">
                    <div 
                        onClick={handleToggleDimension} 
                        className="dimensionButton"
                        style={{
                            pointerEvents: isTransitioning ? 'none' : 'auto',
                            cursor: isTransitioning ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {portfolioDimension ? '3D' : '2D'}
                    </div>
                </div>
            </div>
        </div>
    )
}